import { RichText as RT, type RenderBlock, type RichTextContext, type TextRun } from "@bundle/core";
import type { Block } from "@bundle/contracts";
import { useEffect, useState } from "react";
import { useT } from "../i18n/I18nProvider.tsx";
import { IconButton, InlineText, Stack, Text, useAnnouncer, type InlineRun } from "../primitives/index.ts";

export type RichTextProps = {
  blocks: Block[];
  ctx: RichTextContext;
  onUserPress?: (userId: string) => void;
  onChannelPress?: (channelId: string) => void;
  /** A user group mention was clicked (open the group card). */
  onGroupPress?: (groupId: string) => void;
  /** Larger text for emoji-only messages. */
  jumbo?: boolean;
};

function toInline(runs: TextRun[], p: RichTextProps): InlineRun[] {
  return runs.map((r) => {
    const run: InlineRun = { text: r.text };
    if (r.bold === true) run.bold = true;
    if (r.italic === true) run.italic = true;
    if (r.strike === true) run.strike = true;
    if (r.code === true) run.code = true;
    if (r.link !== undefined) run.link = r.link;
    if (r.chip !== undefined) run.chip = r.chip;
    const userId = r.userId;
    const channelId = r.channelId;
    const groupId = r.groupId;
    if (groupId !== undefined && p.onGroupPress !== undefined) {
      const press = p.onGroupPress;
      run.onPress = () => press(groupId);
    }
    if (userId !== undefined && p.onUserPress !== undefined) {
      const press = p.onUserPress;
      run.onPress = () => press(userId);
    }
    if (channelId !== undefined && p.onChannelPress !== undefined) {
      const press = p.onChannelPress;
      run.onPress = () => press(channelId);
    }
    return run;
  });
}

/** A ``` block: monospaced, with a Copy button shown on hover / focus (Slack); it reads "Copied" for a moment after. */
function CodeBlock({ text, index }: { text: string; index: number }) {
  const t = useT();
  const announce = useAnnouncer();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);
  const copy = () => {
    void navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true);
        announce(t("message.code.copied"));
      },
      () => undefined,
    );
  };
  return (
    <Stack direction="horizontal" align="start" gap="2" paddingX="3" paddingY="2" radius="md" background="bg.surface" border="border.subtle" hoverGroup>
      <Stack grow>
        <Text variant="footnote" mono selectable>
          {text}
        </Text>
      </Stack>
      <Stack revealOnHover>
        <IconButton
          icon={copied ? "check" : "copy"}
          size="sm"
          accessibilityLabel={t(copied ? "message.code.copied" : "message.code.copy")}
          onPress={copy}
          testID={`code-copy-${index}`}
        />
      </Stack>
    </Stack>
  );
}

function BlockView({ block, p, index }: { block: RenderBlock; p: RichTextProps; index: number }) {
  switch (block.type) {
    case "paragraph":
      return <InlineText runs={toInline(block.runs, p)} variant={p.jumbo === true ? "title1" : "body"} />;
    case "code":
      return <CodeBlock text={block.text} index={index} />;
    case "quote":
      return (
        <Stack direction="horizontal" align="stretch" gap="3">
          <Stack radius="full" background="border.strong" paddingX="0.5" />
          <Stack grow gap="1">
            {block.blocks.map((b, i) => (
              <BlockView key={i} block={b} p={p} index={i} />
            ))}
          </Stack>
        </Stack>
      );
    case "list":
      return (
        <Stack gap="0.5" paddingX="1">
          {block.items.map((item, i) => (
            <Stack key={i} direction="horizontal" align="start" gap="2">
              <Text color="text.secondary">{block.ordered ? `${i + 1}.` : "•"}</Text>
              <Stack grow gap="1">
                {item.map((b, j) => (
                  <BlockView key={j} block={b} p={p} index={index + j} />
                ))}
              </Stack>
            </Stack>
          ))}
        </Stack>
      );
  }
}

/** Renders a server-parsed message body (paragraphs, quotes, lists, code; mention/channel/agent chips). */
export function RichText(p: RichTextProps) {
  const t = useT();
  // A channel I can't see reads "private channel" (Slack), in my language.
  const blocks = RT.renderBlocks(p.blocks, { ...p.ctx, privateChannelLabel: t("richtext.privateChannel") });
  return (
    <Stack gap="1.5">
      {blocks.map((b, i) => (
        <BlockView key={i} block={b} p={p} index={i} />
      ))}
    </Stack>
  );
}
